/**
 * Collection-native adapter for the DSP export pipeline
 * (modifyLayers/dspColorFilter/dspExportUtils.js: generateDspPDFContent /
 * exportDspToDOCX).
 *
 * Same role as `collectionExportData` plays for hexagons, pointed at the DSP
 * report instead: the model path's `prepareDspExportData` reads model-specific
 * context (project, pickedTask, and OpenLayers DSP features) that doesn't exist
 * for a collection, so rather than bend that function to accept two shapes this
 * module builds the exact `exportData` object it returns from collection-native
 * inputs. The two generators are consumed as-is — they only read that shape, so
 * this adapter is the entire integration surface.
 *
 * The DSP report is used rather than the hexagon one because its Project
 * Information block carries "Total Plots" and "Total Footprint Area" instead of
 * a hexagon cell size, which is exactly the difference between the two
 * aggregations.
 *
 * @module collectionDspExportData
 */

import {
	describeClassificationMethod,
	describeCoverageGaps,
} from '@routes/userRoutes/projects/singleProject/components/modifyLayers/hexagonColorFilter/matrixFilter/matrixClassification';
import { COLLECTION_REPORT_LABELS } from '@routes/userRoutes/projects/singleProject/components/modifyLayers/hexagonColorFilter/matrixFilter/reportLabels';
import { collectionReportHeader } from './collectionReportHeader';
import { objectLabel } from './transformUtils';

/**
 * @param {Object} params
 * @param {Object} params.filter - active matrix/simple filter config
 * @param {Object} params.classificationResults - per-class results from
 *   useHexagonClassification
 *   (`{ [classId]: { count, area, objectArea, sharePercentage } }`), where `area`
 *   is the summed plot footprint and `objectArea` is the detected object area the
 *   cost figures are priced on
 * @param {string} params.collectionName
 * @param {Object} params.project - current project (area, created_at)
 * @param {Array<{key: string, label: string}>} params.objects - the collection's detection objects
 * @param {string} params.userName - display name of the current user (report "Analyst" field)
 * @param {string} params.projectNotes
 * @param {Object} params.currency - resolved currency ({ id, symbol, label })
 * @param {Array<{id,label,unit}>} params.availableProperties - property catalog
 *   for classificationMethod label resolution
 * @param {Object} params.coverageGaps - result of getSimpleFilterCoverageGaps /
 *   getMatrixFilterCoverageGaps (matching filter.type), computed by the caller so
 *   the report reflects the same gaps shown in the editor
 * @param {{ detections: Object, plotCount: number, totalFootprintArea: number,
 *   totalObjectCount: number }|null} params.summary - result of collectionDspSummary(plots)
 * @returns {Object} exportData, matching the shape generateDspPDFContent /
 *   exportDspToDOCX expect, plus `pricingBasisNote` so the report states that cost
 *   figures are priced on detected object area rather than plot footprint,
 *   `percentageLabel` so the share column is headed "% of plots" as in the panel,
 *   and `containerAreaLabel`/`areaLabel` (+ per-row `containerArea`,
 *   `totalContainerArea`) so the Classification Summary prints "Footprint" and
 *   "Object area" as separate columns
 */
export const prepareCollectionDspExportData = ({
	filter,
	classificationResults,
	collectionName,
	project,
	objects,
	userName,
	projectNotes,
	currency,
	availableProperties,
	coverageGaps,
	summary,
}) => {
	// Per-class cost summary — same math as the model DSP path, reading our
	// per-class `objectArea` instead of `totalArea`. The area being priced is
	// detected object area, not the summed plot footprints that `area` holds, so
	// the report reproduces the panel's cost arithmetic. The outbound keys stay
	// `area` / `totalArea`: that's the shared generators' contract.
	//
	// The double-counting caveat moves with the basis rather than disappearing —
	// an object photographed by two overlapping images is counted in both plots'
	// `sum_object_area`, so the total remains an upper bound.
	const summaryData = (filter?.classes ?? []).map(cls => {
		const r = classificationResults?.[cls.id];
		const area = r?.objectArea || 0;
		const price = cls.pricePerM2 || 0;
		return {
			label: cls.label,
			color: cls.color,
			count: r?.count || 0,
			area,
			// The summed plot footprint, printed as its own column beside the priced
			// `area` above — the same "Footprint" + "Object area" pair the panel's
			// results table shows. Carried separately because a plot has two areas
			// and only one of them is priced; reading the cost off the footprint
			// would overstate it several-fold, which is exactly what a single
			// unlabelled area column invites. Never read by the cost arithmetic.
			containerArea: r?.area || 0,
			// Outbound key stays `percentage` — that's the generators' contract.
			percentage: r?.sharePercentage || 0,
			pricePerM2: price,
			totalCost: area * price,
		};
	});

	const classificationCost = summaryData.reduce((sum, s) => sum + s.totalCost, 0);
	const totalArea = summaryData.reduce((sum, s) => sum + s.area, 0);
	// Classified footprint only — deliberately not `summary.totalFootprintArea`,
	// which covers every plot including the unclassified ones and so would not
	// total the column above it.
	const totalContainerArea = summaryData.reduce((sum, s) => sum + s.containerArea, 0);

	const customPriceFields = (filter?.customPriceFields || []).map(f => ({
		label: f.label,
		price: f.price || 0,
		cost: totalArea * (f.price || 0),
	}));
	const customPriceTotalCost = customPriceFields.reduce((sum, f) => sum + f.cost, 0);
	const totalCost = classificationCost + customPriceTotalCost;

	// Detection stats — from the whole-collection plot summary (every detected
	// object, not just the ones the active color-filter references).
	const detections = summary?.detections ?? {};
	const totalObjectCount = summary?.totalObjectCount ?? 0;

	let totalHeightSum = 0;
	let totalHeightCount = 0;
	const detectionStats = Object.entries(detections).map(([objectKey, det]) => {
		if (Number.isFinite(det.mean_height) && det.count) {
			totalHeightSum += det.mean_height * det.count;
			totalHeightCount += det.count;
		}
		const percentage =
			totalObjectCount > 0 ? ((det.count / totalObjectCount) * 100).toFixed(1) : 0;
		return {
			name: objectLabel(objects, objectKey),
			count: det.count || 0,
			meanHeight: Number.isFinite(det.mean_height) ? det.mean_height.toFixed(2) : null,
			percentage,
		};
	});
	const overallMeanHeight =
		totalHeightCount > 0 ? (totalHeightSum / totalHeightCount).toFixed(2) : null;

	// Report header identity: the collection's display name
	const collectionLabel = collectionName?.trim();

	const header = collectionReportHeader({ project, objects });

	return {
		summaryData,
		totalCost,
		customPriceFields,
		customPriceTotalCost,
		totalArea,
		totalContainerArea,
		// Collection-native header fields, in place of the model's project/task info.
		projectTitle: collectionName || 'DSP Classification Report',
		projectArea: header.projectArea,
		projectCreated: header.projectCreated,
		userName: userName || 'Unknown',
		modelClasses: header.modelClasses,
		modelDescription: collectionLabel || '-',
		totalObjectCount,
		detectionStats,
		overallMeanHeight,
		labels: COLLECTION_REPORT_LABELS,
		resolution: header.gsd,
		totalPlots: summary?.plotCount ?? 0,
		totalFootprintArea: summary?.totalFootprintArea ?? 0,
		projectNotes,
		currency,
		classificationMethod: describeClassificationMethod(filter, availableProperties),
		coverageGapMessage: describeCoverageGaps(filter, coverageGaps),
		// Same caption the panel shows under its cost summary. The columns below now
		// name both areas, so this note no longer carries the pricing basis on its
		// own — it stays for the overlap caveat, which no column heading can state.
		pricingBasisNote:
			'Priced on detected object area — an object photographed by two overlapping images is counted in both plots, so treat the total as an upper bound.',
		// `sharePercentage` is a share of plot count (CollectionDspLayer passes
		// percentageBasis 'count'), not of the object area beside it — say so in
		// the header, matching the panel's "% of plots" column.
		percentageLabel: '% of plots',
		// Two area columns, named as in the panel's results table: the image
		// footprint the plot covers, then the detected object area every cost figure
		// is priced on. Base unit m² in both, matching the {currency}/m² prices
		// beside them, so the printed multiplication reconciles with the printed
		// cost. Supplying `containerAreaLabel` is what turns the extra column on in
		// the DSP generators; the model path omits it and prints one area.
		containerAreaLabel: 'Footprint (m²)',
		areaLabel: 'Object area (m²)',
	};
};

export default prepareCollectionDspExportData;
